import React, { useCallback, useMemo, useState } from 'react';
import { Modal, ModalBody } from '../ui/index';
import type { ModalProps } from '../ui/Modal/Modal';
import type { StylesConfig } from 'react-select';
import ReactSelect from 'react-select';
import { reactSelectStyles } from '../../lib/form-util';
import { Button, Form, FormSection, FormTitle, ModalFooter, Text } from '../ui';
import type { ClientId } from '../core/ClientId';
import type { PatchShipment } from '../Scenario';
import type { VoyageAttributeById } from '../core/Core';
import type { ScenarioShipment } from '../../lib/planningData';
import { useUpdateShipmentMutation } from '../../graphql';
import { toast } from 'react-toastify';

interface VoyageAttributeFormProps {
  onClose: () => void;
  clientId?: ClientId;
  shipment?: ScenarioShipment | null;
  hasChange?: boolean;
  voyageAttributeById?: VoyageAttributeById;
  patchShipment?: (clientId: ClientId, patch: PatchShipment) => void;
}

export type VoyageAttributeModalProps = VoyageAttributeFormProps &
  Partial<ModalProps>;

const dot = (color = 'transparent') => ({
  alignItems: 'center',
  display: 'flex',

  ':before': {
    backgroundColor: color,
    borderRadius: 10,
    content: '" "',
    display: 'block',
    marginRight: 8,
    height: 16,
    width: 16,
  },
});

const colorStyles: StylesConfig<{
  color: string;
  value: number;
  label: string;
}> = {
  control: (styles) => ({
    ...styles,
    backgroundColor: 'white',
    width: '28rem',
  }),
  option: (styles, { data }) => ({
    ...styles,
    ...dot(data.color),
    fontSize: 'small',
  }),
  input: (styles) => ({ ...styles, ...dot() }),
  placeholder: (styles) => ({ ...styles, ...dot('#ccc') }),
  singleValue: (styles, { data }) => ({ ...styles, ...dot(data.color) }),
};

const VoyageAttributeModal = (props: VoyageAttributeFormProps) => {
  const {
    clientId,
    shipment,
    hasChange,
    voyageAttributeById,
    patchShipment,
    onClose,
    ...restProps
  } = props;

  const { id: dbId, voyageAttributeId } = shipment ?? {};
  const [value, setValue] = useState<number | null>(voyageAttributeId ?? null);

  const [updateShipmentMutation] = useUpdateShipmentMutation();
  const onSubmit = useCallback(async () => {
    try {
      // Update the db if the shipment is persisted
      if (dbId && !hasChange) {
        await updateShipmentMutation({
          variables: {
            shipmentId: dbId,
            patch: { voyageAttributeId: value },
          },
        });
      }
      // Update the local scenario object if the shipment is not persisted
      if (clientId) {
        patchShipment?.(clientId, { voyageAttributeId: value });
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to set voyage attribute');
    } finally {
      onClose();
    }
  }, [
    dbId,
    clientId,
    hasChange,
    value,
    onClose,
    updateShipmentMutation,
    patchShipment,
  ]);

  const voyageAttributeOptions = useMemo(
    () =>
      Array.from(voyageAttributeById?.values() ?? [])
        .filter((i) => i.isActive) // Filter out inactive options
        .map((i) => ({
          value: i.id,
          label: `${i.name}`,
          color: i.color,
          isDisabled: !i.isActive,
        })),
    [voyageAttributeById],
  );

  if (!clientId || !voyageAttributeById) {
    return null;
  }

  return (
    <Modal isOpen onClose={onClose} {...restProps} height="10em" width="30em">
      <ModalBody p={0} overflowX="visible" overflowY="visible">
        <Form
          style={{ overflowY: 'visible', border: 'none' }}
          onSubmit={onSubmit}
          id="voyage-attribute-form"
        >
          <FormSection borderBottomStyle="none">
            <FormTitle mb={3}>
              <Text fontSize="1em" fontWeight="bold">
                Set voyage attribute
              </Text>
            </FormTitle>
            <ReactSelect
              isClearable
              value={
                voyageAttributeOptions.find((i) => i.value === value) || null
              }
              onChange={(option) => {
                if (option && 'value' in option) {
                  setValue(option.value);
                } else {
                  setValue(null);
                }
              }}
              components={{ DropdownIndicator: null }}
              options={voyageAttributeOptions}
              placeholder="Select an attribute"
              styles={{ ...reactSelectStyles, ...colorStyles }}
            />
          </FormSection>
        </Form>
      </ModalBody>
      <ModalFooter zIndex={-1}>
        <Button variant="negative" onPress={onClose} marginRight="3">
          Discard
        </Button>
        <div style={{ flexGrow: 1 }} />
        <Button
          variant="primary"
          type="submit"
          form="voyage-attribute-form"
          onPress={onSubmit}
        >
          Set & Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default VoyageAttributeModal;
